import { zodResolver } from '@hookform/resolvers/zod';
import { ForwardRefRenderFunction, forwardRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  openTimingPlaceModalAction,
  selectIsTimingPlaceModalOpen,
  useAppSelector,
} from '../../../../../../redux';
import { StopWithDetails } from '../../../../../../types';
import {
  FormActionButtons,
  ReasonForChangeForm,
} from '../../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../../common/Forms/NavigationBlocker';
import {
  FormColumn,
  HorizontalSeparator,
  Visible,
} from '../../../../../common/LayoutComponents';
import { CreateTimingPlaceModal } from '../../../Common';
import { StopAreaDetailsSection } from '../BasicDetailsStopAreaFields';
import { StopBasicDetailsFormState, schema } from './schema';
import { StopLabelAndLocationFormRow } from './StopLabelAndLocationFormRow';
import { StopOtherDetailsFormRow } from './StopOtherDetailsFormRow';
import { StopTypesFormRow } from './StopTypesFormRow';

type StopBasicDetailsFormComponentProps = {
  readonly className?: string;
  readonly defaultValues: Partial<StopBasicDetailsFormState>;
  readonly onSubmit: (state: StopBasicDetailsFormState) => void;
  readonly stop: StopWithDetails;
  readonly isHybrid?: boolean;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
};

const StopBasicDetailsFormComponent: ForwardRefRenderFunction<
  HTMLFormElement,
  StopBasicDetailsFormComponentProps
> = (
  {
    className,
    defaultValues,
    onSubmit,
    stop,
    isHybrid,
    onCancel,
    testIdPrefix,
  },
  ref,
) => {
  const dispatch = useDispatch();
  const isTimingPlaceModalOpen = useAppSelector(selectIsTimingPlaceModalOpen);

  const methods = useForm<StopBasicDetailsFormState>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'StopBasicDetailsForm');
  const { handleSubmit } = methods;

  const onTimingPlaceCreated = (timingPlaceId: UUID) => {
    methods.setValue('timingPlaceId', timingPlaceId, { shouldDirty: true });
  };

  const openTimingPlaceModal = () => {
    dispatch(openTimingPlaceModalAction());
  };

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={handleSubmit(onSubmit)} ref={ref}>
        <FormColumn>
          {stop && <StopAreaDetailsSection stop={stop} />}
          <StopLabelAndLocationFormRow />
          <HorizontalSeparator />
          <StopOtherDetailsFormRow
            onClickOpenTimingSettingsModal={openTimingPlaceModal}
            isTransportModeLocked={isHybrid}
          />
          <StopTypesFormRow />
          <HorizontalSeparator />
          <ReasonForChangeForm />
        </FormColumn>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !methods.formState.isDirty || methods.formState.isSubmitting
          }
          isSubmitting={methods.formState.isSubmitting}
          variant="infoContainer"
        />
      </form>
      <Visible visible={isTimingPlaceModalOpen}>
        <CreateTimingPlaceModal onTimingPlaceCreated={onTimingPlaceCreated} />
      </Visible>
    </FormProvider>
  );
};

export const StopBasicDetailsForm = forwardRef(StopBasicDetailsFormComponent);
